import { all, put, takeEvery, fork } from "redux-saga/effects";
import { apiURL } from "../actions/helpers";
import axios from "axios";
import moment from "moment";

// Action Types
export const ALERT_DETAIL_LOAD = "ALERT_DETAIL/LOAD";
export const ALERT_DETAIL_LOAD_SUCCESS = "ALERT_DETAIL/LOAD/SUCCESS";
export const ALERT_DETAIL_LOAD_FAILURE = "ALERT_DETAIL/LOAD/FAILURE";

// Action Creators
export const alertDetailLoad = (contractId) => ({
  type: ALERT_DETAIL_LOAD,
  contractId,
});

export const alertDetailLoadSuccess = (data) => ({
  type: ALERT_DETAIL_LOAD_SUCCESS,
  data,
});

export const alertDetailLoadFailure = (error) => ({
  type: ALERT_DETAIL_LOAD_FAILURE,
  error,
});

// Format price to currency string
const formatPrice = (price) => {
  if (price === null || price === undefined) return null;
  if (typeof price === "number") {
    return `$${price.toFixed(2)}`;
  }
  // If it's already a string (like "$0.56"), return as is
  if (typeof price === "string") {
    // If it's a numeric string, format it
    const numPrice = parseFloat(price);
    if (!isNaN(numPrice)) {
      return `$${numPrice.toFixed(2)}`;
    }
    return price;
  }
  return price;
};

// Format days to expiration as "41d"
const formatDaysToExpiration = (days) => {
  if (days === null || days === undefined) return "";
  return `${days}d`;
};

// Format expiration date from "2025-11-21" to "11/21"
const formatExpirationDate = (dateString) => {
  if (!dateString) return "";
  return moment(dateString).format("M/D");
};

// Format contract title from API data
const formatContractTitle = (
  ticker,
  strikePrice,
  optionType,
  expirationDate,
) => {
  const formattedExpiration = formatExpirationDate(expirationDate);
  return `${ticker} $${strikePrice} ${optionType} ${formattedExpiration}`;
};

// Format timestamp from ISO string to "AS OF 15 OCT AT 12:53 (EST)"
const formatTimestamp = (isoString) => {
  if (!isoString) return "";
  const date = moment(isoString);
  const day = date.format("D");
  const month = date.format("MMM").toUpperCase();
  const time = date.format("h:mm A");
  return `AS OF ${day} ${month} AT ${time} (EST)`;
};

// Format date from "2025-10-10" to "ON 15 OCT"
const formatActivityDate = (dateString) => {
  if (!dateString) return "";
  const date = moment(dateString);
  const day = date.format("D");
  const month = date.format("MMM").toUpperCase();
  return `ON ${day} ${month}`;
};

// Transform API response to component format
const transformApiResponse = (apiData) => {
  return {
    // Basic alert info
    sentiment: apiData.sentiment?.toUpperCase() || "BULLISH",
    strategyType: apiData.strategy?.toUpperCase() || "LONG CALL",
    alertText: formatContractTitle(
      apiData.ticker,
      apiData.strikePrice,
      apiData.optionType,
      apiData.expirationDate,
    ),
    expiresIn: formatDaysToExpiration(apiData.daysToExpiration),
    contractId: apiData.contractSymbol || "",
    symbol: apiData.ticker || "",
    buyPrice: formatPrice(apiData.aiBuyPrice),
    takeProfit: formatPrice(apiData.takeProfit),
    stopLoss: formatPrice(apiData.stopLoss),
    lastUpdated: formatTimestamp(apiData.lastUpdatedTimestamp),

    // Options Vitals
    optionsVitals: {
      bid: formatPrice(apiData.optionsVitals?.bid),
      bidSize: apiData.optionsVitals?.bidSize?.toString() || "0",
      mid: formatPrice(apiData.optionsVitals?.mid),
      ask: formatPrice(apiData.optionsVitals?.ask),
      askSize: apiData.optionsVitals?.askSize?.toString() || "0",
      askLotSize: apiData.optionsVitals?.askLotSize?.toString() || "0",
      intrinsic: formatPrice(apiData.optionsVitals?.intrinsicValue),
      extrinsic: formatPrice(apiData.optionsVitals?.extrinsicValue),
      volume: apiData.optionsVitals?.volume?.toString() || "0",
      openInterest: apiData.optionsVitals?.openInterest?.toString() || "0",
    },

    // Overview
    overview: {
      companyName: apiData.overview?.companyName || "",
      underlyingPrice: formatPrice(apiData.overview?.underlyingPrice),
      sector: apiData.overview?.sector || "",
      sectorChangePercent: apiData.overview?.sectorChangePercent || "",
    },

    // AI Reasoning
    aiReasoning: apiData.aiReasoning || "",

    // Unusual Activity
    unusualActivity: {
      asOfDate: formatActivityDate(apiData.unusualActivity?.asOfDate),
      volumeVsAverage: {
        period: apiData.unusualActivity?.volumeVsAverage?.period || {},
        percentageChange:
          apiData.unusualActivity?.volumeVsAverage?.percentageChange || "",
        todayVolume:
          apiData.unusualActivity?.volumeVsAverage?.todayVolume || "",
        averageVolume:
          apiData.unusualActivity?.volumeVsAverage?.averageVolume || "",
      },
      darkPoolActivity: {
        value: apiData.unusualActivity?.darkPoolActivity?.value || "",
        percentageChange:
          apiData.unusualActivity?.darkPoolActivity?.percentageChange || "",
      },
    },
  };
};

// Sagas
function* fetchAlertDetail(action) {
  try {
    const { contractId } = action;
    const response = yield axios.get(
      `${apiURL}/ai-alerts/trade/${contractId}`,
      {
        withCredentials: true,
      },
    );

    const transformedData = transformApiResponse(response.data);
    yield put(alertDetailLoadSuccess(transformedData));
  } catch (error) {
    yield put(alertDetailLoadFailure(error));
  }
}

function* listenAlertDetailLoad() {
  yield takeEvery(ALERT_DETAIL_LOAD, fetchAlertDetail);
}

// Root Saga
export function* saga() {
  yield all([fork(listenAlertDetailLoad)]);
}

// Initial State
const INIT_STATE = {
  data: null,
  loading: false,
  error: null,
};

// Reducer
const reducer = (state = INIT_STATE, action) => {
  switch (action.type) {
    case ALERT_DETAIL_LOAD:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case ALERT_DETAIL_LOAD_SUCCESS:
      return {
        ...state,
        data: action.data,
        loading: false,
        error: null,
      };
    case ALERT_DETAIL_LOAD_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error,
      };
    default:
      return state;
  }
};

// Selectors
export const getAlertDetailData = (state) => state.alertDetail.data;
export const getAlertDetailLoading = (state) => state.alertDetail.loading;
export const getAlertDetailError = (state) => state.alertDetail.error;

export default reducer;
